import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EventCard = ({ event, onRegister, onViewDetails, userTier }) => {
  const isEligible = event?.tierRequirement === 'All' || 
    (userTier === 'Premium' && ['Premium', 'All Access', 'Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'All Access' && ['All Access', 'Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'Flex' && ['Flex', 'Intro']?.includes(event?.tierRequirement)) ||
    (userTier === 'Intro' && event?.tierRequirement === 'Intro');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'text-success bg-success/10';
      case 'Limited': return 'text-warning bg-warning/10';
      case 'Full': return 'text-error bg-error/10';
      case 'Registered': return 'text-primary bg-primary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'Premium': return 'bg-accent text-accent-foreground';
      case 'All Access': return 'bg-primary text-primary-foreground';
      case 'Flex': return 'bg-secondary text-secondary-foreground';
      case 'Intro': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-wellness hover:shadow-wellness-md transition-wellness overflow-hidden">
      {/* Event Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={event?.image}
          alt={event?.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeColor(event?.tierRequirement)}`}>
            {event?.tierRequirement}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event?.status)}`}>
            {event?.status}
          </span>
        </div>
        {event?.featured && (
          <div className="absolute bottom-3 left-3">
            <span className="px-2 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium flex items-center">
              <Icon name="Star" size={12} className="mr-1" />
              Featured
            </span>
          </div>
        )}
      </div>
      {/* Event Content */}
      <div className="p-4">
        {/* Event Type & Date */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Icon 
              name={event?.type === 'Virtual' ? 'Video' : 'MapPin'} 
              size={14} 
              className="text-muted-foreground" 
            />
            <span className="text-xs text-muted-foreground font-medium">
              {event?.type}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {event?.date}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-foreground mb-2 line-clamp-2">
          {event?.title}
        </h3>

        {/* Expert Info */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={14} color="white" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{event?.expert?.name}</p>
            <p className="text-xs text-muted-foreground">{event?.expert?.credentials}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {event?.description}
        </p>

        {/* Event Details */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{event?.duration}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {event?.registered}/{event?.capacity}
              </span>
            </div>
          </div>
          {event?.price > 0 ? (
            <span className="text-sm font-semibold text-foreground">
              ${event?.price}
            </span>
          ) : (
            <span className="text-sm font-semibold text-success">Free</span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(event)}
            className="flex-1"
          >
            View Details
          </Button>
          {event?.status === 'Registered' ? (
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              disabled
            >
              Registered
            </Button>
          ) : (
            <Button
              variant={isEligible && event?.status !== 'Full' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onRegister(event)}
              disabled={!isEligible || event?.status === 'Full'}
              className="flex-1"
            >
              {!isEligible ? 'Upgrade Required' : event?.status === 'Full' ? 'Full' : 'Register'}
            </Button>
          )}
        </div>

        {/* Member Testimonial */}
        {event?.testimonial && (
          <div className="mt-3 p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground italic">
              "{event?.testimonial?.text}"
            </p>
            <p className="text-xs text-muted-foreground font-medium mt-1">
              - {event?.testimonial?.author}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;